/** COMMON IMPORTS */
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { BrowserRouter as Router, Switch, Route, useLocation } from "react-router-dom";
import { UserContext } from "./components/reuseableComponents/auth/UserContext";
import { EnvironmentProvider } from "./configuration/EnvironmentContext";
import URL404 from "./components/reuseableComponents/URL404";
import Footer from "./components/reuseableComponents/navigation/Footer";
import Header from "./components/reuseableComponents/navigation/Header";
import SelectedWaterSystemHeader from "./components/reuseableComponents/SelectedWaterSystemHeader";

/** ROUTE IMPORTS */
import { ddwAdminRoutes } from "./routes/ddwAdminRoutes.js";
import { enforcementRoutes } from "./routes/enforcementRoutes";
import { engineeringRoutes } from "./routes/engineeringRoutes.js";
import { publicRoutes } from "./routes/publicRoutes";
import { portalRoutes } from "./routes/portalRoutes";
import { siteVisitRoutes } from "./routes/siteVisitRoutes.js";

/** UTAH DESIGN SYSTEM IMPORTS */
import "@utahdts/utah-design-system/css";
import "@utahdts/utah-design-system-header/css";
import "./css/utahDesign.css";

/** Dynamic Data for Menu Items */
import { useApolloClient, useLazyQuery } from "@apollo/client";
import { GET_WATER_SYSTEM_SUMMARY } from "./queries/publicQrys/SystemHeaderViewQrys";
import { GET_LOGGEDIN_USER_QUERY } from "./queries/ddwAdminQrys/UserManagementQrys";

/** Sidebar Imports */
import WaterSystemSearchModal from "./components/reuseableComponents/WaterSystemSearchModal";
import { WaterSystemSearchModalProvider, useWaterSystemSearchModal } from "./components/reuseableComponents/WaterSystemSearchModalContext.js";
import WaterSystemGuardRoute from "./components/reuseableComponents/WaterSystemGuardRoute";

/** Status Bar Imports */
import StatusBar from "./components/reuseableComponents/StatusBar";
import { HoverProvider } from "./context/HoverContext.js";
import { getPwsIdCookie } from "./utilities/commonFunctions/pwsIdCookie";
import { setUserProfile } from "./components/reuseableComponents/auth/UserProfiles.js";

/**
 * Utility to retrieve a cookie value by name
 */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

/**
 * Announces page navigations to screen readers via a visually-hidden aria-live region.
 * Must be rendered inside <Router> so useLocation is available.
 */
const RouteAnnouncer = () => {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      const h1 = document.querySelector('h1');
      setAnnouncement(h1 ? `Navigated to ${h1.textContent}` : 'Page changed');
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
    <span
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="visually-hidden"
    >
      {announcement}
    </span>
  );
};

const CookieWaterSystemRestorer = ({ setSelectedWaterSystem, user }) => {
  const { setIsRestoringFromCookie, setSelectedWaterSystem: setContextWaterSystem } = useWaterSystemSearchModal();
  const [getWaterSystemFromCookie, { data: cookieSystemData, error: cookieError }] = useLazyQuery(GET_WATER_SYSTEM_SUMMARY);
  const client = useApolloClient();
  const prevUserIdRef = useRef(user?.umdId ?? null);

  const applyWaterSystem = useCallback((waterSystem) => {
    sessionStorage.setItem("selectedWaterSystem", JSON.stringify(waterSystem));
    setSelectedWaterSystem(waterSystem);
    setContextWaterSystem(waterSystem);
  }, [setSelectedWaterSystem, setContextWaterSystem]);

  useEffect(() => {
    if (!sessionStorage.getItem("selectedWaterSystem")) {
      const pwsId = getPwsIdCookie();
      if (pwsId) {
        getWaterSystemFromCookie({ variables: { pwsId } });
      } else {
        setIsRestoringFromCookie(false);
      }
    }
  }, [getWaterSystemFromCookie, setIsRestoringFromCookie]);

  useEffect(() => {
    const prevUserId = prevUserIdRef.current;
    const currentUserId = user?.umdId ?? null;
    prevUserIdRef.current = currentUserId;

    if (!prevUserId && currentUserId) {
      const pwsId = getPwsIdCookie();
      if (pwsId) {
        client.query({
          query: GET_WATER_SYSTEM_SUMMARY,
          variables: { pwsId },
          fetchPolicy: "network-only",
        }).then(({ data }) => {
          if (data?.getWaterSystemSummary) {
            applyWaterSystem(data.getWaterSystemSummary);
          }
        }).catch((error) => {
          console.warn("Failed to restore water system from cookie on login:", error);
        });
      }
    }
  }, [user, client, applyWaterSystem]);

  useEffect(() => {
    if (cookieSystemData?.getWaterSystemSummary) {
      applyWaterSystem(cookieSystemData.getWaterSystemSummary);
      setIsRestoringFromCookie(false);
    }
  }, [cookieSystemData, applyWaterSystem, setIsRestoringFromCookie]);

  useEffect(() => {
    if (cookieError) {
      setIsRestoringFromCookie(false);
    }
  }, [cookieError, setIsRestoringFromCookie]);

  return null;
};

const App = () => {
  const client = useApolloClient();
  const [isSyncing, setIsSyncing] = useState(false);

  const [user, setUser] = useState(
    localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : { isPublic: true }
  );

  const [rawMenuItemsArray, setRawMenuItemsArray] = useState([]);
  const [selectedWaterSystem, setSelectedWaterSystem] = useState(
    sessionStorage.getItem("selectedWaterSystem")
      ? JSON.parse(sessionStorage.getItem("selectedWaterSystem"))
      : null
  );
  const [redirectPath, setRedirectPath] = useState(null);
  const hasInitialized = useRef(false);

  /** * Orchestrates the sequential loading of User
   */
  const initializeApp = useCallback(async () => {
    // if (isSyncing) return;
    console.log("Initializing application...");

    try {
      setIsSyncing(true);
      const v2AuthCookie = getCookie("v2Authenticated");
      const refreshCookie = getCookie("refresh_token");

      let currentUser = user;

      const userFromStorage = localStorage.getItem("user");
      const savedSystem = sessionStorage.getItem("selectedWaterSystem");
      if (savedSystem) {
        setSelectedWaterSystem(JSON.parse(savedSystem));
      }

      // Fetch user data if we don't already have it, but have successfully logged in to either environment
      if (!userFromStorage && (v2AuthCookie === "true" || !!refreshCookie)) {
        console.log("User not in storage but authenticated. Fetching user data...");

        try {
          const { data: userData } = await client.query({
            query: GET_LOGGEDIN_USER_QUERY,
            fetchPolicy: 'network-only',
          });

          if (userData?.getLoggedInUser) {
            console.log("User fetched successfully:", userData.getLoggedInUser.umdId);
            const validatedUser = setUserProfile(userData.getLoggedInUser);

            // Update both React state AND local reference
            setUser(validatedUser);
            currentUser = validatedUser;

            // Persist to localStorage for future loads
            localStorage.setItem("user", JSON.stringify(validatedUser));
            localStorage.setItem("auth", "true");

            if (validatedUser.permissions.length === 0) {
              console.warn("User has no permissions. Redirecting to access request.");
              setRedirectPath("/public/request-access");
            }
          } else {
            console.warn("GET_LOGGEDIN_USER_QUERY returned no user data");
          }
        } catch (userFetchErr) {
          console.error("Failed to fetch user data:", userFetchErr);
          throw userFetchErr; // Don't continue if user fetch fails
        }
      } else if (userFromStorage) {
        console.log("User already in storage. Using cached data.");
        currentUser = JSON.parse(userFromStorage);
      } else {
        console.log("Not authenticated (v2AuthCookie missing). User will be null.");
      }

      console.log("Current user after initialization:", currentUser);

    } catch (err) {
      console.error("App initialization failed:", err);
    } finally {
      setIsSyncing(false); // Unlocks the UI
    }
  }, [client, user]);

  useEffect(() => {
    if (!isSyncing && !hasInitialized.current) {
      initializeApp();
      hasInitialized.current = true;
    }
  }, [isSyncing, initializeApp]);

  const providerUser = useMemo(() => ({
    user,
    setUser,
    isSyncing // Pass this down so PrivateRoute can see it
  }), [user, setUser, isSyncing]);

  return (
    <EnvironmentProvider>
      <UserContext.Provider value={providerUser}>
        <WaterSystemSearchModalProvider menuItemsArray={rawMenuItemsArray}>
          <HoverProvider>
            <Router>
              <nav aria-label="Skip links">
                <a href="#main-content" className="skip-to-main-content">Skip to main content</a>
              </nav>
              <CookieWaterSystemRestorer setSelectedWaterSystem={setSelectedWaterSystem} user={user} />
              <WaterSystemSearchModal
                selectedWaterSystem={selectedWaterSystem}
                setSelectedWaterSystem={setSelectedWaterSystem}
              />
              <div className="waterlink-container">
                <Header
                  setRawMenuItemsArray={setRawMenuItemsArray}
                  rawMenuItemsArray={rawMenuItemsArray}
                  selectedWaterSystem={selectedWaterSystem}
                  providerUser={providerUser}
                  redirectPath={redirectPath}
                  setRedirectPath={setRedirectPath}
                />
                <main id="main-content" className="main-content-container d-flex flex-column">
                  <RouteAnnouncer />
                  <SelectedWaterSystemHeader />
                  {isSyncing ? (
                    // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
                    <div className="utah-id-syncing" role="status" aria-live="polite"><em>Checking Login Status...</em></div>
                  ) : (
                    <Switch>
                      {/* PUBLIC ROUTES */}
                      {publicRoutes.map((entry) =>
                        entry.requiresWaterSystem ? (
                          <WaterSystemGuardRoute
                            key={entry.path}
                            exact
                            path={entry.path}
                            component={entry.component}
                            redirectPath={entry.redirectPath}
                          />
                        ) : (
                          <Route
                            key={entry.path}
                            exact
                            path={entry.path}
                          >
                            <entry.component
                              selectedWaterSystem={selectedWaterSystem}
                              setSelectedWaterSystem={setSelectedWaterSystem}
                            />
                          </Route>
                        )
                      )}
                      {/*SECURE ROUTES*/}
                      {ddwAdminRoutes.map((entry) => (
                        <Route
                          key={entry.path}
                          exact
                          path={entry.path}
                          component={entry.component}
                        />
                      ))}
                      {enforcementRoutes.map((entry) => (
                        <Route
                          key={entry.path}
                          exact
                          path={entry.path}
                          component={entry.component}
                        />
                      ))}
                      {siteVisitRoutes.map((entry) => (
                        <Route
                          key={entry.path}
                          exact
                          path={entry.path}
                          component={entry.component}
                        />
                      ))}
                      {engineeringRoutes.map((entry) =>
                      entry.requiresWaterSystem ? (
                        <WaterSystemGuardRoute
                          key={entry.path}
                          exact={entry.exact !== false}
                          path={entry.path}
                          component={entry.component}
                        />
                      ) : (
                        <Route
                          key={entry.path}
                          exact={entry.exact !== false}
                          path={entry.path}
                        >
                          <entry.component
                            selectedWaterSystem={selectedWaterSystem}
                            setSelectedWaterSystem={setSelectedWaterSystem}
                          />
                        </Route>
                      ))}
                      {portalRoutes.map((entry) => (
                        <Route
                          key={entry.path}
                          exact
                          path={entry.path}
                          component={entry.component}
                        />
                      ))}
                      <Route component={URL404} />
                    </Switch>
                  )}
                  <StatusBar />
                </main>
              </div>
              <Footer />
            </Router>
          </HoverProvider>
        </WaterSystemSearchModalProvider>
      </UserContext.Provider>
    </EnvironmentProvider>
  );
}

export default App;
